import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { AlertTriangle, ArrowLeft, HelpCircle, Library, Loader2, Plus, XCircle } from 'lucide-react';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { SubjectSelect } from './SubjectSelect';
import type { Subject } from 'modules/subject/types';
import type { BankDraftQuestion } from 'modules/learnArtifacts';
import { useBulkCreateBankQuestions } from 'modules/questionBank';
import type {
  CreateBankQuestionData,
  ImportIssue,
  ImportPreviewResult,
  ImportRow,
} from 'modules/questionBank/api';
import { FEATURES, useFeature, useOpenUpgradeForFeature } from 'modules/entitlements';
import { PremiumBadge } from 'components/entitlements/PremiumBadge';
import { isAdvancedQuestionType } from 'pages/QuestionBank/questionTypeMeta';
import { GeneratedQuestionCard } from './GeneratedQuestionCard';
import { ImportAiAdjustedBadge } from './ImportAiAdjustedBadge';
import { QuestionCardBoundary } from './QuestionCardBoundary';
import { createBlankQuestion, draftIssue, type DraftIssue } from './importDraft';

/**
 * Review step for a file import — the same editable surface as an AI-generated
 * batch, rather than a read-only list of row labels. Rendered by
 * `ImportReviewPage`; the toolbar sticks to the top of the page scroll.
 *
 * A file is a *draft*, not a submission: extraction mangles the odd formula,
 * and papers are routinely distributed with no answer key at all. Reviewing
 * through the generated-questions card means the teacher can fix a prompt,
 * pick the correct answers, change a type and choose the destination subject
 * before anything is written — instead of importing blind and then editing
 * dozens of questions one at a time in the bank.
 *
 * Nothing is persisted until Add to bank: unlike the AI draft this has no
 * server-side artifact to autosave into, so edits live in this component and
 * the whole batch commits through the ordinary bulk-create path.
 */

/** Types whose answer key lives in `correctAnswer`, so an empty one means "unanswered". */
const ANSWER_BEARING_TYPES = new Set([
  'MULTIPLE_CHOICE',
  'MULTIPLE_RESPONSE',
  'TRUE_FALSE',
  'SHORT_ANSWER',
  'MATCHING',
]);

function needsAnswer(question: BankDraftQuestion): boolean {
  if (!ANSWER_BEARING_TYPES.has(question.type)) return false;
  const answer = question.correctAnswer;
  if (Array.isArray(answer)) return answer.length === 0;
  if (typeof answer === 'string') return answer.trim() === '';
  return !answer || Object.keys(answer).length === 0;
}

const ISSUE_BADGE_KEY: Record<DraftIssue, string> = {
  prompt: 'import_needs_prompt_badge',
  options: 'import_needs_options_badge',
  answer: 'import_no_answer_badge',
};

/** Stable within one preview; `rowNumber` alone repeats across text-file questions. */
const draftId = (row: ImportRow, index: number) => `import-${index}-${row.rowNumber}`;

type ImportedQuestion = CreateBankQuestionData & { subjectName?: string };

/**
 * An imported question becomes a draft. The id is local to this dialog, and
 * the subject fields are dropped — both are re-applied at commit, and the API
 * rejects non-whitelisted keys.
 */
function toDraftQuestion(imported: ImportedQuestion, id: string): BankDraftQuestion {
  const { subjectName: _name, subjectId: _subject, ...question } = imported;
  return {
    ...question,
    id,
    points: question.points ?? 1,
    correctAnswer: question.correctAnswer ?? '',
  } as BankDraftQuestion;
}

/**
 * The topic the file gave a question. It rides along on the draft untouched (the draft type
 * has no field for it) and is sent with the question on commit.
 */
const topicOf = (question: BankDraftQuestion): string | undefined =>
  (question as BankDraftQuestion & { topic?: string }).topic;

/** The AI-only picture description the file gave a question; it rides along like the topic. */
const imageDescriptionOf = (question: BankDraftQuestion): string | undefined =>
  (question as BankDraftQuestion & { aiImageDescription?: string }).aiImageDescription;

/** Row warnings about a topic that was dropped; the server's English text is shown as written. */
const TOPIC_WARNING_CODES = new Set(['invalid_topic', 'topic_too_long']);

const toDraft = (row: ImportRow, index: number): BankDraftQuestion =>
  toDraftQuestion(row.question as ImportedQuestion, draftId(row, index));

/** Server notices the client has localized text for; the rest show the server's English. */
const noticeText = (notice: ImportIssue, t: TFunction): string =>
  notice.code.startsWith('ai_repair') || notice.code === 'looks_like_not_a_paper'
    ? t(`import_notice_${notice.code}`, { count: notice.count ?? 0, defaultValue: notice.message })
    : notice.message;

export interface ImportReviewProps {
  preview: ImportPreviewResult;
  subjects: Subject[];
  defaultSubjectId?: string;
  /** Leaves the review without importing; the batch is discarded. */
  onBack: () => void;
  /** Called once the batch is in the bank. */
  onDone: () => void;
}

/** Where a new question goes relative to the row it was added from. */
type InsertSide = 'above' | 'below';

export const ImportReview: React.FC<ImportReviewProps> = ({
  preview,
  subjects,
  defaultSubjectId,
  onBack,
  onDone,
}) => {
  const { t } = useTranslation();

  const importable = useMemo(
    () => preview.rows.filter((row) => row.question !== null),
    [preview.rows],
  );
  const failed = useMemo(() => preview.rows.filter((row) => row.question === null), [preview.rows]);

  // Seeded once, from the preview this page was opened with. Re-seeding on a
  // prop change would discard the edits this page exists to collect — they live
  // nowhere else until commit.
  const [questions, setQuestions] = useState<BankDraftQuestion[]>(() => importable.map(toDraft));
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(importable.map((row, index) => draftId(row, index))),
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [subjectId, setSubjectId] = useState(defaultSubjectId ?? '');
  // Questions the AI replaced, by draft id, with the importer's version to return to (null: none to return to).
  const [aiAdjusted, setAiAdjusted] = useState<Map<string, ImportedQuestion | null>>(
    () =>
      new Map(
        importable.flatMap((row, index) =>
          row.repair ? [[draftId(row, index), row.repair.original] as const] : [],
        ),
      ),
  );
  // Questions typed in here rather than read from the file — the only ones that can be removed outright.
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());
  const addedCount = useRef(0);

  const bulkCreate = useBulkCreateBankQuestions();
  // Advanced types are Growth+. Locking those rows means a mixed file imports
  // its basic questions instead of the whole batch failing on the server.
  const canAdvancedTypes = useFeature(FEATURES.ADVANCED_QUESTION_TYPES);
  const openUpgrade = useOpenUpgradeForFeature();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  /**
   * Subjects the file itself named and the server resolved. They win over the
   * picker, which fills in the rest — so a spreadsheet with a Subject column
   * keeps its own routing.
   */
  const subjectFromFile = useMemo(() => {
    const map = new Map<string, string>();
    importable.forEach((row, index) => {
      const resolved = row.question?.subjectId;
      if (resolved) map.set(draftId(row, index), resolved);
    });
    return map;
  }, [importable]);

  /** Why a topic the file carried was left out, by draft id. */
  const topicWarnings = useMemo(() => {
    const map = new Map<string, ImportIssue[]>();
    importable.forEach((row, index) => {
      const issues = row.warnings.filter((issue) => TOPIC_WARNING_CODES.has(issue.code));
      if (issues.length > 0) map.set(draftId(row, index), issues);
    });
    return map;
  }, [importable]);

  const isLocked = (question: BankDraftQuestion) =>
    !canAdvancedTypes && isAdvancedQuestionType(question.type);

  const updateQuestion = (id: string, patch: Partial<BankDraftQuestion>) =>
    setQuestions((previous) =>
      previous.map((question) => {
        if (question.id !== id) return question;
        const next = { ...question, ...patch };
        // `undefined` in a patch means "clear this field" — a type switch does
        // exactly that — so drop those keys rather than persisting nulls.
        for (const [key, value] of Object.entries(patch)) {
          if (value === undefined) delete (next as Record<string, unknown>)[key];
        }
        return next;
      }),
    );

  /** Puts the question back exactly as the importer read it; the AI badge goes with it. */
  const revertAiAdjustment = (id: string) => {
    const original = aiAdjusted.get(id);
    if (!original) return;
    setQuestions((previous) => previous.map((question) => (question.id === id ? toDraftQuestion(original, id) : question)));
    setAiAdjusted((previous) => {
      const next = new Map(previous);
      next.delete(id);
      return next;
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setQuestions((previous) => {
      const from = previous.findIndex((question) => question.id === active.id);
      const to = previous.findIndex((question) => question.id === over.id);
      if (from === -1 || to === -1) return previous;
      const next = [...previous];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  /** Adds a blank question beside `anchorId` (or as the only one) and opens it for typing. */
  const insertQuestion = (anchorId: string | null, side: InsertSide) => {
    addedCount.current += 1;
    const blank = createBlankQuestion(`import-new-${addedCount.current}`);
    setQuestions((previous) => {
      const anchor = anchorId === null ? -1 : previous.findIndex((question) => question.id === anchorId);
      if (anchor === -1) return [...previous, blank];
      const at = side === 'above' ? anchor : anchor + 1;
      return [...previous.slice(0, at), blank, ...previous.slice(at)];
    });
    setAddedIds((previous) => new Set(previous).add(blank.id));
    setSelectedIds((previous) => new Set(previous).add(blank.id));
    setEditingId(blank.id);
  };

  const deleteQuestion = (id: string) => {
    setQuestions((previous) => previous.filter((question) => question.id !== id));
    setAddedIds((previous) => {
      const next = new Set(previous);
      next.delete(id);
      return next;
    });
    setSelectedIds((previous) => {
      const next = new Set(previous);
      next.delete(id);
      return next;
    });
    setEditingId((previous) => (previous === id ? null : previous));
  };

  const toggleSelected = (id: string) =>
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectable = questions.filter((question) => !isLocked(question));
  const selected = selectable.filter((question) => selectedIds.has(question.id));
  const lockedCount = questions.length - selectable.length;
  const unanswered = selected.filter(needsAnswer).length;
  // The API refuses the whole batch over one of these, so they hold the button instead.
  const incomplete = selected.filter((question) => draftIssue(question) !== null).length;

  const handleImport = () => {
    const items = selected.map((question) => {
      const { id, ...rest } = question;
      return {
        ...rest,
        subjectId: subjectFromFile.get(id) ?? subjectId,
      } as CreateBankQuestionData;
    });
    if (items.length === 0) return;
    // `mutateAsync`, not a per-call `onSuccess`: React Query drops those once this
    // page has unmounted, and the page still has to clear the batch it handed over.
    // Failures are toasted by the hook, so the rejection has nothing left to do.
    bulkCreate.mutateAsync(items).then(onDone, () => undefined);
  };

  const canImport =
    selected.length > 0 && Boolean(subjectId) && incomplete === 0 && !bulkCreate.isPending;

  /** Why the button is disabled, stated rather than left to guesswork. */
  const blockedReason =
    selected.length === 0 && questions.length > 0
      ? t('select_at_least_one_question')
      : !subjectId && questions.length > 0
        ? t('choose_a_subject_to_save_into')
        : incomplete > 0
          ? t('import_n_incomplete', { count: incomplete })
          : null;

  return (
    <div className="mx-auto w-full max-w-4xl pb-16">
      <h1 className="mb-3 text-xl font-semibold text-foreground">{t('review_imported_questions')}</h1>

      {/* Toolbar — sticks to the top of the page while the list scrolls. */}
      <div className="sticky top-0 z-10 mb-2 flex flex-wrap items-center gap-3 border-b border-border bg-background py-2.5">
        <Button variant="ghost" size="sm" onClick={onBack} disabled={bulkCreate.isPending}>
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          {t('back')}
        </Button>

        <span className="text-sm font-medium text-foreground">
          {t('n_of_m_selected', { selected: selected.length, total: questions.length })}
        </span>

        {unanswered > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
            <HelpCircle className="h-3.5 w-3.5" />
            {t('import_n_need_answer', { count: unanswered })}
          </span>
        )}

        {lockedCount > 0 && (
          <PremiumBadge
            featureKey={FEATURES.ADVANCED_QUESTION_TYPES}
            label={t('import_locked_count', { count: lockedCount })}
            tooltip={t('import_locked_hint')}
          />
        )}

        <span className="flex-1" />

        {/* Destination — chosen with the questions in front of you, the same way
            the AI review dialog does it. */}
        <div className="flex items-center gap-1.5 max-w-full">
          <label htmlFor="import-subject" className="text-xs text-muted-foreground shrink-0">
            {t('save_to')}
          </label>
          <SubjectSelect
            id="import-subject"
            value={subjectId}
            onChange={setSubjectId}
            subjects={subjects}
            showCount
            triggerClassName="h-auto min-h-8 min-w-[200px] max-w-[320px] text-xs"
          />
        </div>

        <Button size="sm" onClick={handleImport} disabled={!canImport}>
          {bulkCreate.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Library className="mr-2 h-4 w-4" />
          )}
          {t('add_n_to_bank', { count: selected.length })}
        </Button>
      </div>

      {blockedReason && (
        <p className="mb-2 text-xs text-amber-600 dark:text-amber-500">{blockedReason}</p>
      )}

      <div className="w-full">
        {preview.notices.length > 0 && (
          <div className="mb-3 space-y-1.5 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/60 dark:bg-amber-950/30">
            {preview.notices.map((notice) => (
              <p
                key={notice.code}
                className="flex items-start gap-2 text-xs text-amber-800 dark:text-amber-200"
              >
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {noticeText(notice, t)}
              </p>
            ))}
          </div>
        )}

        {/* Rows the parser could not read. Listed rather than dropped, so the
            count being committed is never quietly smaller than the file. */}
        {failed.length > 0 && (
          <details className="mb-3 rounded-lg border border-border bg-muted/30 p-3">
            <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
              {t('import_skipped_count', { count: failed.length })}
            </summary>
            <ul className="mt-2 space-y-1.5">
              {failed.map((row) => (
                <li key={`${row.rowNumber}-${row.label}`} className="flex items-start gap-2 text-xs">
                  <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />
                  <span className="min-w-0">
                    <span className="tabular-nums text-muted-foreground">{row.rowNumber}</span>{' '}
                    <span className="text-foreground">{row.label}</span>
                    {row.errors.map((issue) => (
                      <span key={issue.code} className="block text-red-600 dark:text-red-400">
                        {issue.message}
                      </span>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}

        {questions.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-12">
            <p className="text-sm text-muted-foreground">{t('no_questions_in_draft')}</p>
            <Button variant="outline" size="sm" onClick={() => insertQuestion(null, 'below')}>
              <Plus className="mr-1.5 h-4 w-4" />
              {t('add_question')}
            </Button>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={questions.map((question) => question.id)}
              strategy={verticalListSortingStrategy}
            >
              {questions.map((question, index) => {
                const locked = isLocked(question);
                return (
                  <QuestionCardBoundary key={question.id} index={index + 1} type={question.type}>
                    <GeneratedQuestionCard
                      question={question}
                      index={index}
                      selected={!locked && selectedIds.has(question.id)}
                      editing={editingId === question.id}
                      locked={false}
                      onAddAbove={() => insertQuestion(question.id, 'above')}
                      onAddBelow={() => insertQuestion(question.id, 'below')}
                      onDelete={addedIds.has(question.id) ? () => deleteQuestion(question.id) : undefined}
                      subjectId={subjectFromFile.get(question.id) ?? subjectId}
                      footnote={
                        <>
                          {topicOf(question) && (
                            <p data-testid="import-topic" className="mt-1.5 text-xs text-muted-foreground">
                              {t('question_topic_label')}: {topicOf(question)}
                            </p>
                          )}
                          {imageDescriptionOf(question) && (
                            <p data-testid="import-image-description" className="mt-1.5 text-xs text-muted-foreground">
                              {t('ai_image_description')}: {imageDescriptionOf(question)}
                            </p>
                          )}
                          {topicWarnings.get(question.id)?.map((issue, issueIndex) => (
                            <p
                              key={`${issue.code}-${issueIndex}`}
                              data-testid="import-topic-warning"
                              className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400"
                            >
                              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                              {issue.message}
                            </p>
                          ))}
                        </>
                      }
                      notice={
                        <>
                          {aiAdjusted.has(question.id) && (
                            <ImportAiAdjustedBadge
                              onRevert={
                                aiAdjusted.get(question.id) ? () => revertAiAdjustment(question.id) : undefined
                              }
                            />
                          )}
                          {locked ? (
                            <PremiumBadge
                              featureKey={FEATURES.ADVANCED_QUESTION_TYPES}
                              tooltip={t('import_locked_hint')}
                            />
                          ) : draftIssue(question) ? (
                            <span
                              className={cn(
                                'inline-flex items-center gap-1 rounded px-1.5 py-0.5',
                                'text-[11px] font-medium',
                                'bg-warning-50 text-warning-800 dark:bg-warning-900/30 dark:text-warning-300',
                              )}
                            >
                              <HelpCircle className="h-3 w-3" />
                              {t(ISSUE_BADGE_KEY[draftIssue(question) as DraftIssue])}
                            </span>
                          ) : needsAnswer(question) ? (
                            <span
                              className={cn(
                                'inline-flex items-center gap-1 rounded px-1.5 py-0.5',
                                'text-[11px] font-medium',
                                'bg-warning-50 text-warning-800 dark:bg-warning-900/30 dark:text-warning-300',
                              )}
                            >
                              <HelpCircle className="h-3 w-3" />
                              {t('import_no_answer_badge')}
                            </span>
                          ) : null}
                        </>
                      }
                      // A premium row offers the upgrade rather than a checkbox
                      // that silently refuses to tick.
                      onToggleSelected={() =>
                        locked
                          ? openUpgrade(FEATURES.ADVANCED_QUESTION_TYPES)
                          : toggleSelected(question.id)
                      }
                      onToggleEditing={() =>
                        setEditingId((previous) => (previous === question.id ? null : question.id))
                      }
                      onChange={(patch) => updateQuestion(question.id, patch)}
                    />
                  </QuestionCardBoundary>
                );
              })}
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
};

export default ImportReview;
